<script setup lang="ts">
import { nextTick } from 'vue'

import {
  createStudentAdLoginRequest,
  loginStudentToForm,
} from '@/composables/current-student-advanced-standing-form/useStudentFormApi'
import { useStudentLoginState } from '@/composables/current-student-advanced-standing-form/useStudentLoginState'
import {
  impersonationLoginState,
} from '@/models/current-student-advanced-standing-form/appSession'
import type {
  PublicFormApiResponse,
  StudentFormLoadData,
} from '@/models/current-student-advanced-standing-form/api'
import { useCurrentStudentApplicationStore } from '@/stores/current-student-advanced-standing-form/applicationStore'
import { useCurrentStudentCourseSelectionStore } from '@/stores/current-student-advanced-standing-form/courseSelectionStore'
import StudentDetailsLoginStep from './StudentDetailsLoginStep.vue'

const props = defineProps<{
  handleAuthSessionError: (error: unknown, behavior: 'redirect' | 'reload') => boolean
  getErrorMessage: (error: unknown) => string
}>()

const emit = defineEmits<{
  'update-deploy-user': [response: PublicFormApiResponse<unknown>]
  'login-success': []
}>()

const {
  pageLoadMessage,
  studentDetailsVisible,
  loginErrorMessage,
  loginProgressVisible,
  loginProgressMessage,
  loginPending,
  loginValidated,
  impersonateMessageVisible,
  username,
  password,
  pageLoadMessageClass,
  loginProgressClass,
  startPageLoad,
  showPageLoadError,
  applyMaintenanceState,
  applyLoginMaintenanceState,
  applyImpersonationState,
  resetLoginInputState,
  startLoginValidation,
  finishLoginRequest,
  completeLoginValidation,
  failLoginValidation,
  clearPassword,
} = useStudentLoginState()

const applicationStore = useCurrentStudentApplicationStore()
const courseSelectionStore = useCurrentStudentCourseSelectionStore()

function onLoginInput(): void {
  resetLoginInputState()
}

function applyImpersonationQueryState(url: string): void {
  applyImpersonationState(impersonationLoginState(url))
}

function loginPendingOrHidden(): boolean {
  return loginPending.value || !studentDetailsVisible.value
}

function isLoginValidated(): boolean {
  return loginValidated.value
}

async function processLogin(): Promise<void> {
  startLoginValidation()

  try {
    const result = await loginStudentToForm(createStudentAdLoginRequest(username.value, password.value))
    emit('update-deploy-user', result)
    finishLoginRequest()

    if ((result.data.maintenance_mode_enabled ?? 0) > 1) {
      applyLoginMaintenanceState(result.data)
      return
    }

    completeLoginValidation()
    applicationStore.applyLoginData(result.data)
    courseSelectionStore.loadCourseListing(result.data.user.course_list, result.data.submission)
    clearPassword()
    emit('login-success')
  } catch (error) {
    if (props.handleAuthSessionError(error, 'redirect')) return

    failLoginValidation(props.getErrorMessage(error))
    scrollToValidateProgressAfterRender()
  }
}

function scrollToValidateProgressAfterRender(): void {
  void nextTick(() => {
    window.requestAnimationFrame(() => {
      document.querySelector('#validateProgress')?.scrollIntoView()
    })
  })
}

defineExpose({
  startPageLoad,
  showPageLoadError,
  applyMaintenanceState: (
    data: StudentFormLoadData,
    user: PublicFormApiResponse<StudentFormLoadData>['user'],
  ) => applyMaintenanceState(data, user),
  applyImpersonationQueryState,
  loginPendingOrHidden,
  isLoginValidated,
  processLogin,
})
</script>

<template>
  <StudentDetailsLoginStep
    v-model:username="username"
    v-model:password="password"
    :impersonate-message-visible="impersonateMessageVisible"
    :page-load-message-class="pageLoadMessageClass"
    :page-load-message="pageLoadMessage"
    :student-details-visible="studentDetailsVisible"
    :login-pending="loginPending"
    :login-error-message="loginErrorMessage"
    :login-progress-visible="loginProgressVisible"
    :login-progress-class="loginProgressClass"
    :login-progress-message="loginProgressMessage"
    @login-input="onLoginInput"
  />
</template>
